import { App, Form, Input, Modal } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'

import type { InputRef } from 'antd'

/**
 * Модалка назви: «Нова папка» і «Перейменувати». Відмову бека (зайнята
 * назва, заборонені символи) показує під полем — людина виправляє, не
 * закриваючи вікно.
 *
 * У файлі при перейменуванні виділено назву без розширення, як у провіднику.
 */
export const DiskNameModal = observer(() => {
    const { message } = App.useApp()
    const modalState = store.nameModal
    const open = modalState !== null
    const renaming = modalState?.mode === 'rename' ? modalState.node : null
    const [name, setName] = useState('')
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<InputRef>(null)

    useEffect(() => {
        if (!open) return

        setName(renaming?.name ?? '')
        setError(null)
    }, [open, renaming])

    const afterOpen = (visible: boolean): void => {
        if (!visible) return

        const input = inputRef.current?.input
        if (!input) return

        input.focus()

        if (renaming) {
            const dot = renaming.type === 'file' ? renaming.name.lastIndexOf('.') : -1
            input.setSelectionRange(0, dot > 0 ? dot : renaming.name.length)
        }
    }

    const submit = async (): Promise<void> => {
        // Enter у полі й кнопка «Зберегти» не мають дати двох запитів
        if (store.isBusy) return

        const value = name.trim()

        if (value === '') {
            setError('Вкажіть назву')

            return
        }

        if (renaming && value === renaming.name) {
            store.closeNameModal()

            return
        }

        const result = renaming ? await store.rename(renaming, value) : await store.createFolder(value)

        if (!result.ok) {
            setError(result.error)

            return
        }

        store.closeNameModal()
        void message.success(renaming ? 'Перейменовано' : `Папку «${value}» створено`)
    }

    return (
        <Modal
            open={open}
            title={renaming ? 'Перейменувати' : 'Нова папка'}
            okText={renaming ? 'Зберегти' : 'Створити'}
            cancelText="Скасувати"
            confirmLoading={store.isBusy}
            onOk={() => void submit()}
            onCancel={() => store.closeNameModal()}
            afterOpenChange={afterOpen}
            destroyOnHidden
            width={440}
        >
            <Form layout="vertical" onFinish={() => void submit()}>
                <Form.Item
                    label="Назва"
                    validateStatus={error ? 'error' : ''}
                    {...(error ? { help: error } : {})}
                    style={{ marginBottom: 8 }}
                >
                    <Input
                        ref={inputRef}
                        value={name}
                        maxLength={255}
                        placeholder={renaming ? '' : 'Наприклад, Договори 2026'}
                        onChange={(event) => {
                            setName(event.target.value)
                            if (error) setError(null)
                        }}
                        data-testid="disk-name-input"
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
})
